import React, { useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { MeasuringCylinder } from './MeasuringCylinder';

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

interface PouringCylinderProps {
  isPouring: boolean;
  pourProgress: number;
  showAtRest: boolean;
  liquidLevelMlAtRest: number;
  liquidColor?: string;
}

const REST_POS: [number, number, number] = [-1.0, -0.335, 0.2];
const REST_ROT: [number, number, number] = [0, Math.PI / 8, 0];
const POUR_POS: [number, number, number] = [0.18, 1.10, -0.2];
const POUR_ROT: [number, number, number] = [0, -Math.PI / 2, -1.05];

export function PouringCylinder({ isPouring, pourProgress, showAtRest, liquidLevelMlAtRest, liquidColor = '#f0f9ff' }: PouringCylinderProps) {
  const groupRef = useRef<THREE.Group>(null);

  let cylinderMl = liquidLevelMlAtRest;
  if (isPouring) {
    if (pourProgress < 0.2) cylinderMl = 50;
    else if (pourProgress < 0.8) cylinderMl = 50 * (1 - (pourProgress - 0.2) / 0.6);
    else cylinderMl = 0;
  }

  useFrame(() => {
    if (!groupRef.current) return;
    let t = 0;
    if (isPouring) {
      if (pourProgress < 0.2) t = easeInOut(pourProgress / 0.2);
      else if (pourProgress < 0.8) t = 1;
      else t = easeInOut((1 - pourProgress) / 0.2);
    }
    groupRef.current.position.set(
      lerp(REST_POS[0], POUR_POS[0], t),
      lerp(REST_POS[1], POUR_POS[1], t),
      lerp(REST_POS[2], POUR_POS[2], t),
    );
    groupRef.current.rotation.set(
      lerp(REST_ROT[0], POUR_ROT[0], t),
      lerp(REST_ROT[1], POUR_ROT[1], t),
      lerp(REST_ROT[2], POUR_ROT[2], t),
    );
  });

  if (!isPouring && !showAtRest) return null;

  return (
    <group ref={groupRef} position={REST_POS} rotation={REST_ROT}>
      <MeasuringCylinder liquidLevelMl={cylinderMl} liquidColor={liquidColor} />
    </group>
  );
}

interface PourStreamProps {
  isPouring: boolean;
  pourProgress: number;
}

export function CylinderWaterStream({ isPouring, pourProgress }: PourStreamProps) {
  const ref = useRef<THREE.Mesh>(null);
  const splashRef = useRef<THREE.Mesh>(null);
  const visible = isPouring && pourProgress >= 0.18 && pourProgress <= 0.82;

  const SPOUT: [number, number, number] = [0.05, 1.18, -0.18];
  const BEAKER_TOP: [number, number, number] = [0, 0.93, -0.2];
  const midY = (SPOUT[1] + BEAKER_TOP[1]) / 2;
  const midX = (SPOUT[0] + BEAKER_TOP[0]) / 2;
  const midZ = (SPOUT[2] + BEAKER_TOP[2]) / 2;
  const dx = BEAKER_TOP[0] - SPOUT[0];
  const dy = BEAKER_TOP[1] - SPOUT[1];
  const dz = BEAKER_TOP[2] - SPOUT[2];
  const length = Math.hypot(dx, dy, dz);
  const angleZ = Math.atan2(dx, -dy);
  const angleX = Math.atan2(dz, -dy);

  useFrame((state) => {
    if (!ref.current) return;
    ref.current.visible = visible;
    if (splashRef.current) splashRef.current.visible = visible;
    if (!visible) return;
    const wob = 1 + Math.sin(state.clock.elapsedTime * 16) * 0.06;
    ref.current.scale.x = wob;
    ref.current.scale.z = wob;
    if (splashRef.current) {
      const pulse = 1 + Math.sin(state.clock.elapsedTime * 22) * 0.18;
      splashRef.current.scale.set(pulse, 1, pulse);
    }
  });

  return (
    <group>
      <mesh
        ref={ref}
        position={[midX, midY, midZ]}
        rotation={[angleX, 0, angleZ]}
      >
        <cylinderGeometry args={[0.012, 0.022, length, 16, 1]} />
        <meshPhysicalMaterial
          color="#bfe3ff"
          transparent
          opacity={0.85}
          transmission={0.92}
          ior={1.33}
          roughness={0.05}
          thickness={0.03}
          attenuationColor="#dff1ff"
          attenuationDistance={0.4}
          depthWrite={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh
        ref={splashRef}
        position={[BEAKER_TOP[0], BEAKER_TOP[1] + 0.005, BEAKER_TOP[2]]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <ringGeometry args={[0.020, 0.045, 24]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.6} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}
